import { isDestroyed, isDestroying } from '@ember/destroyable';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismComboSelectInput } from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import type { PropertyTagsUser } from 'lumen/models';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import getAPIErrorMessage from 'lumen/utils/get-api-error-message';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';
import { timeout } from 'lumen/utils/waiters';

interface Option {
  id: string | null;
  label: string;
  user: PropertyTagsUser | null;
}

interface SettingsPropertyTagsAutocompleteUserSignature {
  Args: {
    isDisabled?: boolean;
    isMixed?: boolean;
    selectItem: (option: { user: Option['user'] }) => void;
    subscriptionIds: string[];
    user: PropertyTagsUser | null;
  };
  Element: HTMLElement;
}

export default class SettingsPropertyTagsAutocompleteUser extends Component<SettingsPropertyTagsAutocompleteUserSignature> {
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare store: Services['store'];

  @tracked isOpen = false;
  @tracked options: Option[] = [];
  @tracked optionsFilter = '';
  @tracked page = 0;
  @tracked totalPages = 1;

  fetchOptionsTask = task({ restartable: true }, async () => {
    const requestParams: Record<string, unknown> = {
      namespace: APIGATEWAY,
      typeKey: 'property-tags/autocomplete/users',
      page: this.page,
      market_id: this.hotelAndMarketService.market?.id,
      subscription_id: this.args.subscriptionIds,
    };

    if (this.optionsFilter) {
      requestParams['q'] = this.optionsFilter;
    }

    try {
      const users = await this.store.query<PropertyTagsUser>(
        'property-tags/user',
        // @ts-expect-error: Incorrect type
        requestParams,
      );

      const meta = users.meta as unknown as {
        results_count: number;
        results_page: number;
        results_per_page: number;
      };

      this.totalPages = Math.ceil(meta.results_count / meta.results_per_page);

      return users.map((user) => ({
        id: user.id,
        label: user.name,
        user,
      }));
    } catch (error) {
      this.errorTracking.captureException(error);
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.alerting.showErrorAlert({
        text: getAPIErrorMessage(error, this.intl),
      });

      throw new SilentTaskError(getErrorMessage(error));
    }
  });

  loadMoreTask = task({ restartable: true }, async (): Promise<void> => {
    if (this.page + 1 < this.totalPages) {
      this.page += 1;
      const fetchedOptions = await this.fetchOptionsTask.perform();
      this.options = [...this.options, ...fetchedOptions];
    }
  });

  searchOptionTask = task(
    { restartable: true },
    async (optionsFilter): Promise<void> => {
      await timeout();

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      this.optionsFilter = optionsFilter;
      this.page = 0;
      this.options = await this.fetchOptionsTask.perform();
    },
  );

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get value() {
    const { isMixed, user } = this.args;

    if (isMixed) {
      return {
        id: 'mixed',
        label: this.intl.t('general.mixed'),
        user: null,
      };
    }

    if (user) {
      return {
        id: user.id,
        label: user.name,
        user,
      };
    }

    return null;
  }

  resetValues(): void {
    this.optionsFilter = '';
    this.page = 0;
    this.options = [];
  }

  @action selectOption(option: Option): void {
    this.optionsFilter = '';
    this.args.selectItem(option);
  }

  @action toggleDropdown(): void {
    if (isDestroyed(this) || isDestroying(this)) {
      return;
    }

    this.isOpen = !this.isOpen;

    if (this.isOpen) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.searchOptionTask.perform(this.optionsFilter);
    } else {
      this.resetValues();
    }
  }

  <template>
    <div class="flex flex-col">
      <PrismComboSelectInput
        @disabled={{@isDisabled}}
        @isSearchable={{true}}
        @isSmall={{true}}
        @onChange={{this.selectOption}}
        @onClose={{this.toggleDropdown}}
        @onOpen={{this.toggleDropdown}}
        @onSearch={{this.searchOptionTask.perform}}
        @options={{this.options}}
        @placeholder={{t
          "settings.property_tags.autocomplete_user.placeholder"
        }}
        @unsafe_isClampedToOneLine={{true}}
        @unsafe_onLoadMore={{this.loadMoreTask.perform}}
        @value={{this.value}}
        data-test-id="autocomplete-user"
        ...attributes
      />
    </div>
  </template>
}
